import { WorkGroupActivityStore } from './work-group-activity.store';
import { fb, goBack, routeParams, translate } from '../../../../modules/shared/shared.global-service';
import { Component, computed, effect, EventEmitter, inject, input, Output, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActionButtonsComponent } from '../../../kit/action-buttons/action-buttons.component';
import { DateInputComponent } from '../../../kit/input/date-input/date-input.component';
import { FileUploadBoxComponent } from '../../../kit/file-upload-box/file-upload-box.component';
import {
    InputUserMultiselectComponent,
} from '../../../../modules/shared/components/inputs/input-user-multiselect/input-user-multiselect.component';
import { NumberInputComponent } from '../../../kit/input/text-input/number-input.component';
import { SelectInputComponent } from '../../../kit/input/select-input/select-input.component';
import {
    SelectPersonComponent,
} from '../../../../modules/content/components/common/select-person/select-person.component';
import {
    ServicePatternWysiwygComponent,
} from '../../../../modules/content/components/common/service-pattern-wysiwyg/service-pattern-wysiwyg.component';
import {
    SocialServiceSelectOfferComponent,
} from '../social-service-select-offer/social-service-select-offer.component';
import {
    SocialServiceSelectOfferTableComponent,
} from '../social-service-select-offer/social-service-select-offer-table/social-service-select-offer-table.component';
import {
    WorkDetailSelectServiceComponent,
} from '../../../medical-service/work/work-detail/work-detail-select-service/work-detail-select-service.component';
import {
    WorkIndividualWorkOperationalReportWidgetComponent,
} from '../work-individual-work/work-individual-work-operational-report-widget/work-individual-work-operational-report-widget.component';
import { Validators } from '@angular/forms';
import { WorkOfferUpsertInput } from '@libs/types/src/services/input-type/work/work-offer.upsert-input';
import { AppFormGroup, changeSignal, disableFormControls, formAddText } from '../../../../utils/form.utils';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbService } from 'xng-breadcrumb';
import { ServicePatternStructureUsageEnum } from '@libs/types/src/services/enum/service-pattern-structure-usage.enum';
import { EmploymentParentGroupEnum } from '@libs/types/src/user/enum/employment-parent-group.enum';
import { isOffline } from '../../../../utils/connection.service';
import { ServiceTypeEnum } from '@libs/types/src/services/enum/service-type.enum';
import { CreateWorkInput, UpdateWorkInput, UserGridItemObject, WorkGridItemObject } from '@it2go/types';
import { WorkDataGroupActivityObject } from '@libs/types/src/services/object-type/work/work-data-group-activity.object';
import { ButtonComponent } from '../../../kit/button/button.component';
import { NgTemplateOutlet } from '@angular/common';
import { SplitterComponent } from '../../../kit/splitter/splitter.component';
import {
    WorkCardContentComponent,
} from '../../../../modules/content/components/services/work-card-content/work-card-content.component';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { getWorkDetailFrag } from './work-group-activity.query';
import { dateify, dateifyOrNow } from '../../../../modules/shared/helpers/date.helper';
import {
    WorkDataOperationalReportObject,
} from '@libs/types/src/services/object-type/work/work-data-operational-report.object';
import { WorkTypeEnum } from '@libs/types/src/common/enum/work-type.enum';
import {
    WorkGroupActivityUserTableComponent,
} from './work-group-activity-user-table/work-group-activity-user-table.component';
import { isInvalid } from '../../../../modules/shared/helpers/validate';
import { DefaultCategoryTypeEnum } from '@libs/types/src/file-manager/types/default-category.type';
import { FileKindEnum } from '@libs/types/src/file-manager/enum/file-kind.enum';
import { ExternalType } from '@libs/types/src/file-manager/enum/external-type.enum';
import { checkOk } from '../../../../modules/content/content.error';
import { stripHtml } from '../../../../utils/string.utils';
import { TagsInputComponent } from '../../../kit/input/tags-input/tags-input.component';
import { TranslatePipe } from '@ngx-translate/core';
import { workFolderUri } from '../../folder/works/folder-works-page.component';
import { CardComponent } from '../../../kit/card/card.component';
import {
    WorkParticipantsFolder,
    WorkParticipantsSelectComponent,
} from '../work-participants-select/work-participants-select.component';
import { WorkCreditGroupComponent } from '../work-credit-group/work-credit-group.component';
import {
    WorkOfferTableItem,
} from '../social-service-select-offer/social-service-select-offer-table/social-service-select-offer-table.types';
import { SocialWorkPlanHistoryState } from '../social-works-plan/social-works-plan.types';

@Component({
    selector: 'app-work-group-activity',
    templateUrl: './work-group-activity.component.html',
    styleUrl: './work-group-activity.component.scss',
    standalone: true,
    providers: [WorkGroupActivityStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ActionButtonsComponent,
        DateInputComponent,
        FileUploadBoxComponent,
        InputUserMultiselectComponent,
        NumberInputComponent,
        SelectInputComponent,
        SelectPersonComponent,
        ServicePatternWysiwygComponent,
        SocialServiceSelectOfferComponent,
        SocialServiceSelectOfferTableComponent,
        WorkDetailSelectServiceComponent,
        WorkIndividualWorkOperationalReportWidgetComponent,
        ButtonComponent,
        NgTemplateOutlet,
        SplitterComponent,
        WorkCardContentComponent,
        WorkGroupActivityUserTableComponent,
        TagsInputComponent,
        TranslatePipe,
        CardComponent,
        WorkParticipantsSelectComponent,
        WorkCreditGroupComponent,
    ],
})
export class WorkGroupActivityComponent {

    protected readonly store = inject(WorkGroupActivityStore);
    protected readonly route = inject(ActivatedRoute);
    protected readonly router = inject(Router);
    protected readonly breadcrumbService = inject(BreadcrumbService);

    protected readonly operationalReportComponent = viewChild(WorkIndividualWorkOperationalReportWidgetComponent);
    protected readonly selectUserComponent = viewChild<InputUserMultiselectComponent>('selectUserComponent');
    protected readonly selectOfferComponent = viewChild(SocialServiceSelectOfferComponent);
    protected tagsInput = viewChild<TagsInputComponent>('tagsInput');
    protected readonly fileUploadComponent = viewChild(FileUploadBoxComponent);
    protected readonly usersTableComponent = viewChild<WorkGroupActivityUserTableComponent>('usersTable');
    protected readonly offerTableComponent = viewChild<SocialServiceSelectOfferTableComponent>('offerTableComponent');
    protected readonly creditGroupComponent = viewChild(WorkCreditGroupComponent);

    protected form = new AppFormGroup({
        serviceId: fb.control<number | null>(null, Validators.required),
        personIds: fb.control<number[]>([], Validators.required),
        dateFrom: fb.control<Date | null>(new Date()),
        dateTo: fb.control<Date | null>(null),
        duration: fb.control<number>(0),
        presence: fb.control<string | null>(null),
        users: fb.control<string[]>([]),
        form: fb.control<string | null>(null),
        tags: fb.control<string[]>([]),
        note: fb.control<string>(''),
        offers: fb.control<WorkOfferUpsertInput[]>([], Validators.required),
        subOffers: fb.control<WorkOfferTableItem[]>([], { nonNullable: true }),
        participantIds: fb.control<number[]>([]),
    });

    protected readonly participantsComponent = viewChild(WorkParticipantsSelectComponent);

    protected applicationsComponent = viewChild.required<NumberInputComponent>('applicationsComponent');
    protected serviceIdChange = changeSignal(this.form.controls.serviceId);
    protected dateFromChange = changeSignal(this.form.controls.dateFrom);
    protected dateToChange = changeSignal(this.form.controls.dateTo);
    protected personIdsChange = changeSignal(this.form.controls.personIds);
    protected usersChange = changeSignal(this.form.controls.users);
    protected offersChange = changeSignal(this.form.controls.offers);
    protected offersCodeChange = this.form.signalCodeChange<WorkOfferUpsertInput[]>('offers');
    protected personsCodeChange = this.form.signalCodeChange<number[]>('personIds');
    protected queryParams = toSignal(this.route.queryParams);
    protected readonly selectPersonComponent = viewChild(SelectPersonComponent);
    protected prefix = 'workData.groupActivity.';
    protected existingWork: WorkGridItemObject<WorkDataGroupActivityObject> | null = null;

    public readonly personIdInput = input<number | null>(null);
    @Output() closeCallback = new EventEmitter<void>();

    protected useSplitter = signal(false);
    private readonly planState = history.state?.calendarCorrelationId
        ? history.state as SocialWorkPlanHistoryState
        : null;

    protected allowedForms = computed(() => {
        const forms = this.store.globalGlobalStore.organizationStore.getServiceById(this.serviceIdChange() || 0)?.()?.form || [];

        return forms.map((it) => ({
            name: translate.instant(`service.serviceFormEnum.${it}`),
            value: it,
        }));
    });

    protected refreshMinimums = signal({});
    protected readonly canSave = computed(() => Number(routeParams.workId())
        ? this.store.isAllowed('workGroupActivity.update')()
        : this.store.isAllowed('workGroupActivity.create')());
    protected userMinimumDurations = computed<Record<string, number>>(() => {
        this.refreshMinimums();
        const map: Record<string, number> = {};
        for (const offer of this.form.getRawValue().subOffers || []) {
            for (const userId of offer.users || []) {
                const current = map[userId];
                if (current === undefined || offer.applications < current) {
                    map[userId] = offer.applications;
                }
            }
        }
        return map;
    });

    protected selectedFolders = computed(() => {
        const personIds = this.personsCodeChange();
        const serviceId = this.serviceIdChange();

        return {
            isCodeUpdate: personIds.isCodeUpdate,
            value: this.store.folders().filter((it) => it.serviceId == serviceId && (personIds.value || []).includes(it.personId)),
        };
    });

    protected readonly folderIds = computed(() => this.selectedFolders().value.map(folder => folder.id));
    protected readonly workId = computed(() => Number(routeParams.workId()) || null);
    protected readonly replacedCreditWorkIds = computed(() => this.getReplacedCreditWorkIds());

    protected participantFolders = computed<WorkParticipantsFolder[]>(() =>
        this.selectedFolders().value.map((it) => ({ id: it.id, person: it.person })),
    );

    protected usersStore = new Map<string, UserGridItemObject>();
    protected usersStoreUpdated = signal<object>({});
    protected selectedUsers = computed<UserGridItemObject[]>(() => {
        this.usersStoreUpdated();
        const ids = this.usersChange() || [];

        return (
            ids
                .map((id) => this.usersStore.get(id))
                .filter((it) => it)
        ) as UserGridItemObject[];
    });

    constructor() {

        this.store.personIdFromQuery = this.route.snapshot.queryParams['personId'];
        if (this.store.personIdFromQuery) {
            this.form.patchValue({ personIds: [Number(this.store.personIdFromQuery)] }, { isCodeUpdate: true });
        }

        if (this.planState) {
            this.form.patchValueCode({
                serviceId: this.planState.serviceId,
                personIds: this.planState.personIds,
                dateFrom: this.planState.dateFrom,
                dateTo: this.planState.dateTo,
                users: this.planState.userIds,
                note: this.planState.note,
            });
        }

        effect(() => {
            const selectOffer = this.selectOfferComponent();
            if (selectOffer && this.planState) {
                selectOffer.preselectedOffers.set(this.planState.offers);
            }
        });

        effect(() => {
            const personId = this.personIdInput();
            if (personId && this.personIdsChange()?.length === 0) {
                this.form.patchValue({ personIds: [personId] }, { isCodeUpdate: true });
            }
        });

        effect(() => {
            const from = this.dateFromChange();
            const to = this.dateToChange();
            if (!from || !to) return;

            const duration = Math.round((to.getTime() - from.getTime()) / 60_000);

            this.form.patchValueCode({ duration });
            this.applicationsComponent().setValue(duration);
        });

        effect(() => {
            const users = (this.selectUserComponent()?.userScroll()?.prefetchedItems() || []) as UserGridItemObject[];
            users.forEach((it) => this.usersStore.set(it.id, it));
            this.usersStoreUpdated.set({});
        });

        effect(async () => {
            const userId = this.store.globalGlobalStore.loggedUserStore.currentUserId();
            if (!userId || this.form.getRawValue().users?.length) return;
            this.form.patchValueCode({ users: [userId] });
        });

        effect(() => {
            const personIds = this.personIdsChange() || [];
            const serviceId = this.serviceIdChange() || 0;
            this.store.serviceId.set(serviceId);
            personIds.forEach((personId) => this.store.fetchFolder(personId, serviceId));
        });

        effect(() => {
            const workId = Number(routeParams.workId());
            if (workId) {
                this.breadcrumbService.set('@id', `id|${workId}`);
                this.store.api.getWorks(FilterBuilder.filter({ id: workId }), getWorkDetailFrag);
            }
        });

        effect(() => {
            const params = this.queryParams() || {};
            const workId = params['split'] ?? params['copy'] ?? null;
            this.useSplitter.set(!!params['split']);
            if (workId) {
                this.store.api.getWorks(FilterBuilder.filterById(workId), getWorkDetailFrag);
            }
        });

        effect(() => {
            const work = this.store.data.getWorks()?.items?.[0];
            if (!work || !this.selectOfferComponent()) return;
            this.existingWork = work;

            if (!this.useSplitter()) {
                this.patchFormWithExistingWork(true);
                this.lockBilledFields();
            }
        });

        effect(() => {
            const currentSubOffers = [...(this.form.getRawValue().subOffers || [])];
            const offersChange = this.offersCodeChange();
            const selectedFoldersChange = this.selectedFolders();
            const offers = (offersChange?.value) || [];
            const folders = (selectedFoldersChange?.value) || [];

            if (offersChange.isCodeUpdate && selectedFoldersChange.isCodeUpdate) return;
            if (!offersChange.value || !selectedFoldersChange.value) return;

            const newOffers: any[] = [];
            folders.forEach((folder) => {
                offers.forEach((offer) => {
                    const existingOffer = currentSubOffers.find((it: any) => it.offerId == offer.offerId && it.folder?.id == folder.id);
                    if (existingOffer) {
                        newOffers.push(existingOffer);
                    } else {
                        newOffers.push({ ...offer, folder });
                    }
                });
            });

            this.form.patchValueCode({ subOffers: newOffers });
            this.refreshCreditOffers();
        });
    }

    protected setDuration(duration?: number): void {
        const from = this.dateFromChange();
        if (!from) return;

        const dateTo = new Date(from);
        dateTo.setMinutes(dateTo.getMinutes() + (duration || 0));
        this.form.patchValueCode({ dateTo });
    }

    protected setOffers(offers: WorkOfferUpsertInput[]): void {
        const from = this.dateFromChange();
        if (!from) return;

        let duration = this.selectOfferComponent()!.isDurationAltered
            ? offers.reduce((acc, it) => acc + it.applications, 0)
            : this.form.getRawValue().duration
        ;
        if (duration == 0) {
            const options = this.selectOfferComponent()!.selectedOfferOptions();
            duration = options.reduce((acc, it) => acc + (it.usualApplications || 1), 0);
        }

        const dateTo = new Date(from);
        dateTo.setMinutes(dateTo.getMinutes() + (duration || 0));
        this.form.patchValueCode({ dateTo });
    }

    protected async save(): Promise<void> {
        if (!this.canSave() || this.creditGroupComponent()?.insufficientBalance()) return;
        const persForm = this.selectPersonComponent()!.form;
        const opForm = this.operationalReportComponent()!.form;
        const invalidForm = isInvalid(this.form, opForm, persForm);
        const formValue = this.form.getRawValue();
        const offer = formValue.offers?.[0];
        if (!offer || invalidForm) return;

        const metadata = {
            categoryCode: DefaultCategoryTypeEnum.INDIVIDUAL_WORK,
            note: null,
            date: new Date(),
            kind: FileKindEnum.received,
            accesses: this.serviceIdChange()
                ? [
                    {
                        externalType: ExternalType.service,
                        externalId: String(this.serviceIdChange()),
                    },
                ]
                : null,
        };

        const fileIds = (await this.fileUploadComponent()?.uploadFiles(metadata)) || [];
        const _data = {
            dateFrom: dateifyOrNow(formValue.dateFrom).toISOString(),
            dateTo: dateifyOrNow(formValue.dateTo).toISOString(),
            folderId: null,
            userIds: formValue.users || [],
            fileIds,
            serviceId: formValue.serviceId,
            tags: formValue.tags || [],
            workData: JSON.stringify({
                note: formValue.note,
                form: formValue.form,
            }),
            offers: (this.usersTableComponent()?.getWorkOffers() || []).map((it) => ({
                applications: it.applications,
                dateFrom: it.dateFrom,
                dateTo: it.dateTo,
                executions: it.executions,
                users: it.users || [],
                tags: it.tags || [],
                durationNote: it.durationNote,
                durationPreparation: it.durationPreparation,
                durationSelfReflection: it.durationSelfReflection,
                note: it.note || '',
                offerId: offer.offerId,
                procedures: offer.procedures,
                operations: offer.operations,
            })),
        };

        let res;
        const workId = Number(routeParams.workId());
        if (workId) {
            const data: UpdateWorkInput = this.existingWork?.isBilled
                ? {
                    id: workId,
                    workData: _data.workData,
                    fileIds,
                    tags: formValue.tags || [],
                }
                : { ..._data, id: workId };
            res = await this.store.api.updateWork(data);
            if (!checkOk(res, 'update')) return;
        } else {
            const data = {
                ..._data,
                calendarCorrelationId: this.planState?.calendarCorrelationId,
            } as CreateWorkInput;
            data.workType = WorkTypeEnum.GROUP_ACTIVITY;
            res = await this.store.api.createWork(data, 'id');
            if (!checkOk(res, 'create')) return;
        }

        if (!this.existingWork?.isBilled && this.existingWork?.relatedWorks?.length) {
            await this.store.api.batchDeleteWork({
                ids: this.existingWork.relatedWorks.map((it) => it.id),
            });
        }

        const existingOpRecord = this.existingWork?.relatedWorks?.find(
            (it) => it.workType === WorkTypeEnum.OPERATIONAL_REPORT,
        );
        if (stripHtml(opForm.value.description || '').trim().length) {
            const opFormValue = opForm.getRawValue();
            const opData: CreateWorkInput = {
                dateFrom: dateifyOrNow(formValue.dateFrom).toISOString(),
                dateTo: dateifyOrNow(formValue.dateFrom).toISOString(),
                userIds: [this.store.globalGlobalStore.loggedUserStore.currentUserId() || ''],
                serviceId: formValue.serviceId,
                parentWorkId: this.existingWork?.id || (res as any).id,
                workType: WorkTypeEnum.OPERATIONAL_REPORT,
                workData: JSON.stringify({
                    sendEmail: opFormValue.sendEmail,
                    type: opFormValue.type,
                    form: formValue.form,
                    priority: opFormValue.priority,
                    description: opFormValue.description || '',
                    users: opFormValue.userIds || [],
                }),
            };
            if (this.existingWork?.isBilled && existingOpRecord) {
                await this.store.api.updateWork({ ...opData, id: existingOpRecord.id });
            } else {
                await this.store.api.createWork(opData, 'id');
            }
        } else if (this.existingWork?.isBilled && existingOpRecord) {
            await this.store.api.deleteWork({ id: existingOpRecord.id });
        }

        // Účastníci sedí na položkách aktivity - jen ty mají složku, rodičovská aktivita ne.
        const folderIdByPersonId = this.participantsComponent()?.folderIdByPersonId() ?? new Map();
        const selectedParticipantIds = formValue.participantIds || [];

        const folderItems = this.offerTableComponent()?.items() || [];
        if (!this.existingWork?.isBilled) {
            await Promise.all(folderItems.map(async (offer) => {
                const dateTo = dateifyOrNow(formValue.dateFrom);
                dateTo.setMinutes(dateTo.getMinutes() + offer.applications);
                const itemFolderId = (offer as any).folder?.id;
                const subItemData: CreateWorkInput = {
                    dateFrom: dateifyOrNow(formValue.dateFrom).toISOString(),
                    dateTo: dateTo.toISOString(),
                    userIds: offer.users || [],
                    serviceId: formValue.serviceId,
                    folderId: itemFolderId,
                    parentWorkId: this.existingWork?.id || (res as any).id,
                    workType: WorkTypeEnum.GROUP_ACTIVITY_ITEM,
                    workData: JSON.stringify({}),
                    participantIds: selectedParticipantIds
                        .filter((it) => folderIdByPersonId.get(Number(it)) === Number(itemFolderId)),
                    offers: [{
                        offerId: offer.offerId,
                        payByCredits: offer.payByCredits,
                        applications: offer.applications,
                        dateFrom: offer.dateFrom,
                        dateTo: offer.dateTo,
                        executions: offer.executions,
                        procedures: offer.procedures,
                        operations: offer.operations,
                        durationNote: offer.durationNote,
                        durationPreparation: offer.durationPreparation,
                        durationSelfReflection: offer.durationSelfReflection,
                        presence: offer.presence,
                        type: offer.type,
                        note: offer.note || '',
                        users: offer.users || [],
                        tags: offer.tags || [],
                        targets: offer.targets || [],
                        areas: offer.areas || [],
                    } as any],
                };
                await this.store.api.createWork(subItemData, 'id');
            }));
        }

        await this.fileUploadComponent()?.confirmFiles(fileIds);

        this.close();
    }

    public close() {
        // opened as modal
        if (this.personIdInput()) {
            this.closeCallback.emit();
            // opened from folder
        } else if (this.store.personIdFromQuery) {
            this.router.navigateByUrl(workFolderUri(WorkTypeEnum.GROUP_ACTIVITY, this.store.currentFolderId));
        } else {
            if (this.closeCallback.observed) {
                this.closeCallback.emit();
            } else if (this.planState?.returnUrl) {
                void this.router.navigateByUrl(this.planState.returnUrl);
            } else {
                goBack();
            }
        }
    }

    protected queryTags(search: string): void {
        this.store.api.searchWorkTags({
            search,
            limit: 100,
            serviceId: this.serviceIdChange() || 0,
        });
    }

    protected copyNoteAndTags(): void {
        const note = this.form.value.note || '';
        const formValue = this.form.getRawValue();
        const tags = [...(formValue.tags || [])];
        const updatedSubOffers = (formValue.subOffers || []).map((it: any) => {
            return { ...it, note, tags };
        });
        this.form.patchValueCode({
            subOffers: updatedSubOffers,
        });
        // Because there's no way to just wait for an update
        setTimeout(() => {
            this.offerTableComponent()?.refreshTable(false);
        }, 20);
    }

    private patchFormWithExistingWork(edit: boolean = false): void {
        if (!this.existingWork) return;
        const subItems = (this.existingWork.relatedWorks || [])
            .filter((it) => it.workType == WorkTypeEnum.GROUP_ACTIVITY_ITEM)
        ;

        const personIds = subItems.map((it) => it.personId).filter((it) => it) as any;
        this.form.patchValueCode({
            serviceId: this.existingWork.serviceId,
            dateFrom: dateifyOrNow(this.existingWork.dateFrom),
            dateTo: dateify(this.existingWork.dateTo) || null,
            users: this.existingWork.users.map((it) => it.id),
            note: this.existingWork.workData?.note || '',
            form: this.existingWork.workData?.form || null,
            tags: this.existingWork?.tags || [],
            personIds,
            participantIds: Array.from(new Set(
                subItems.flatMap((it) => (it.participants || []).map((p) => p.id)),
            )),
        });
        this.selectOfferComponent()?.preselectedOffers.set(this.existingWork?.offers);
        this.tagsInput()?.setValue(this.form.getRawValue().tags || []);

        this.form.patchValueCode({
            subOffers: subItems.map((it) => ({
                ...it.offers[0],
                folder: { id: it.folderId },
            } as any)),
        });

        const opReport: WorkGridItemObject<WorkDataOperationalReportObject> | undefined = this.existingWork?.relatedWorks?.find((it) => it.workType == WorkTypeEnum.OPERATIONAL_REPORT) as any;
        if (opReport) {
            this.operationalReportComponent()?.form.patchValue({
                description: opReport.workData?.description || '',
                type: opReport.workData?.type || null,
                priority: opReport.workData?.priority || null,
                userIds: opReport.workData?.userIds || [],
            });
        }

        if (edit) {
            this.form.patchValueCode({
                dateFrom: dateifyOrNow(this.existingWork.dateFrom),
                dateTo: dateify(this.existingWork.dateTo) || null,
            });
        }
        this.refreshCreditOffers();
    }

    private getReplacedCreditWorkIds(): number[] {
        const work: WorkGridItemObject | undefined = this.store.data.getWorks()?.items?.[0];
        if (!this.workId() || !work || work.isBilled) return [];
        return (work.relatedWorks || [])
            .filter(item => item.workType === WorkTypeEnum.GROUP_ACTIVITY_ITEM
                && item.offers?.some(offer => offer.payByCredits === true))
            .map(item => item.id);
    }

    private refreshCreditOffers(): void {
        const offers = this.existingWork?.isBilled ? [] : this.form.controls.subOffers.value;
        this.creditGroupComponent()?.refreshCreditOffers(offers);
    }

    private lockBilledFields(): void {
        if (!this.existingWork?.isBilled) return;

        disableFormControls(this.form, true, ['note']);
    }

    protected updateOfferValues(): void {
        this.refreshMinimums.set({});
        this.refreshCreditOffers();
    }

    protected readonly ServicePatternStructureUsageEnum = ServicePatternStructureUsageEnum;
    protected readonly formAddText = formAddText;
    protected readonly goBack = goBack;
    protected readonly EmploymentParentGroupEnum = EmploymentParentGroupEnum;
    protected readonly isOffline = isOffline;
    protected readonly WorkTypeEnum = WorkTypeEnum;
    protected readonly ServiceTypeEnum = ServiceTypeEnum;

}
